import { useState, useCallback } from "react";
import adminService from "../services/adminService";

/**
 * Custom hook for admin actions (approve/reject registrations)
 * Provides state management and error handling for admin operations
 */
export const useAdminActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastAction, setLastAction] = useState(null);

  /**
   * Clears any existing error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Approves a registration
   * @param {string} registrationId - Registration ID to approve
   * @returns {Promise<Object>} - Approval result
   */
  const approveRegistration = useCallback(async (registrationId) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.approveRegistration(registrationId);
      setLastAction({
        type: "approve",
        registrationId,
        timestamp: new Date(),
        result,
      });
      return result;
    } catch (err) {
      const errorMessage = err.message || "Failed to approve registration";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Rejects a registration
   * @param {string} registrationId - Registration ID to reject
   * @param {string} remark - Rejection reason
   * @returns {Promise<Object>} - Rejection result
   */
  const rejectRegistration = useCallback(async (registrationId, remark) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.rejectRegistration(
        registrationId,
        remark,
      );
      setLastAction({
        type: "reject",
        registrationId,
        remark,
        timestamp: new Date(),
        result,
      });
      return result;
    } catch (err) {
      const errorMessage = err.message || "Failed to reject registration";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Bulk approve multiple registrations
   * @param {Array<string>} registrationIds - Array of registration IDs
   * @returns {Promise<Object>} - Bulk approval results
   */
  const bulkApprove = useCallback(async (registrationIds) => {
    setIsLoading(true);
    setError(null);

    try {
      const result =
        await adminService.bulkApproveRegistrations(registrationIds);
      setLastAction({
        type: "bulkApprove",
        registrationIds,
        timestamp: new Date(),
        result,
      });
      return result;
    } catch (err) {
      const errorMessage =
        err.message || "Failed to bulk approve registrations";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Bulk reject multiple registrations
   * @param {Array<Object>} rejections - Array of {registrationId, remark} objects
   * @returns {Promise<Object>} - Bulk rejection results
   */
  const bulkReject = useCallback(async (rejections) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.bulkRejectRegistrations(rejections);
      setLastAction({
        type: "bulkReject",
        rejections,
        timestamp: new Date(),
        result,
      });
      return result;
    } catch (err) {
      const errorMessage = err.message || "Failed to bulk reject registrations";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Gets registration statistics
   * @returns {Promise<Object>} - Registration statistics
   */
  const getStatistics = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await adminService.getRegistrationStatistics();
      return result;
    } catch (err) {
      const errorMessage = err.message || "Failed to fetch statistics";
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Validates a registration for approval
   * @param {Object} registration - Registration object
   * @returns {Object} - Validation result
   */
  const validateForApproval = useCallback((registration) => {
    return adminService.validateRegistrationForApproval(registration);
  }, []);

  /**
   * Validates a registration for rejection
   * @param {Object} registration - Registration object
   * @param {string} remark - Rejection remark
   * @returns {Object} - Validation result
   */
  const validateForRejection = useCallback((registration, remark) => {
    return adminService.validateRegistrationForRejection(registration, remark);
  }, []);

  return {
    // State
    isLoading,
    error,
    lastAction,

    // Actions
    approveRegistration,
    rejectRegistration,
    bulkApprove,
    bulkReject,
    getStatistics,

    // Validation
    validateForApproval,
    validateForRejection,

    // Utilities
    clearError,
  };
};
